import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyPassword } from "../store/thunks/authThunk";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

export default function LockScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, isPasswordVerified, user } = useSelector(
    (state) => state.auth
  );
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if not authenticated or password is already verified
  useEffect(() => {
    if (!isAuthenticated || isPasswordVerified) {
      navigate("/");
    }
  }, [isAuthenticated, isPasswordVerified, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const email = user?.email;
      if (!email) {
        throw new Error("Email not found");
      }

      const resultAction = await dispatch(verifyPassword({ email, password }));

      if (verifyPassword.fulfilled.match(resultAction)) {
        navigate("/");
      } else {
        setError(resultAction.payload || "Invalid password");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Error verifying password:", err);
    } finally {
      setIsLoading(false);
      setPassword("");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/30">
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            className="mx-auto h-28 w-auto"
            src={`${process.env.PUBLIC_URL}/fancyTherapy.png`}
            alt="HearSay Speech and Hearing Centre"
          />
          <h2 className="mt-8 text-center text-2xl font-semibold tracking-tight text-gray-900">
            Screen Locked
          </h2>
          <div className="mt-2 text-center">
            <span className="text-lg font-medium text-gray-700">
              {user.firstName} {user.lastName}
            </span>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-[360px]">
          <div className="bg-white px-6 py-8 shadow-sm ring-1 ring-gray-900/5 sm:rounded-sm">
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900">
                  Password
                </label>
                <div className="relative mt-1.5">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className={`block w-full rounded-sm border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ${
                      error ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-500'
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                  />
                  {error && (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="rounded-sm bg-red-50 px-3 py-2 text-sm text-red-600">
                  {error}
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center rounded-sm bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  {isLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    'Unlock'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
